/*
 * Local model + wire types for the AI assistant.
 *
 * The backend OpenAPI spec types the assistant message/action endpoints as
 * `void` and does not describe the `CHAT_EVENT` websocket frame, so we model
 * both here following the documented backend contract. All parsing of raw
 * server payloads is funnelled through `normalizeMessage` / `parseChatEvent`
 * so that, if a field name differs from the contract, it changes in one place.
 */

export type MessageRole = 'USER' | 'ASSISTANT' | 'SYSTEM';

export type AssistantStatus =
  'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export interface ToolCall {
  id: string;
  name: string;
  display?: string;
  result_summary?: string;
  status: 'started' | 'finished';
}

export interface QuestionOption {
  value: string;
  label: string;
  meta?: string;
}

export interface AssistantQuestion {
  /** external_id of the question message this answer refers to. */
  question_message: string;
  text: string;
  options: QuestionOption[];
  multi_select?: boolean;
  allow_free_text?: boolean;
  answered_by?: string | null;
  selected_options?: string[];
}

// 'confirm'/'reject' are sent by this UI via decideAction() and match
// ProposedAction.Status CONFIRMED/REJECTED. 'expired' covers a REST-loaded
// action whose status came back EXPIRED (never decided in time).
export type ActionDecision = 'confirm' | 'reject' | 'expired';

export interface AssistantAction {
  /** external_id used for POST /assistant/actions/{id}/. */
  external_id: string;
  kind: string;
  title?: string;
  preview: Record<string, unknown>;
  note?: string;
  decision?: ActionDecision | null;
}

export interface ViewCommand {
  type: string;
  label?: string;
  [key: string]: unknown;
}

export interface AssistantMessage {
  external_id: string;
  client_message_id?: string;
  role: MessageRole;
  content: string;
  status?: AssistantStatus;
  seq: number;
  created_at?: string;
  tool_calls: ToolCall[];
  question?: AssistantQuestion | null;
  actions?: AssistantAction[];
  view_command?: ViewCommand | null;
  error_detail?: string | null;

  // client-only presentation flags
  streaming?: boolean;
  queued?: boolean;
  optimistic?: boolean;
  // The backend creates a real USER message to follow up an action decision
  // (e.g. "(Confirmed action ...; reference ...)") — kept in state but not
  // rendered as a bubble since the ActionCard already shows the outcome.
  isActionDecisionFollowUp?: boolean;
  // content length at the moment an action card was attached, so text streamed
  // after the card renders below it (and text before stays above).
  actionContentLength?: number;
}

export type ChatEventType =
  | 'user_message_ack'
  | 'assistant_delta'
  | 'assistant_completed'
  | 'tool_call'
  | 'view_command'
  | 'action_confirmation_required'
  | 'question_asked'
  | 'error'
  | 'conversation_state'
  | 'conversation_activity';

/** A parsed CHAT_EVENT websocket frame. */
export interface ChatEvent {
  type: ChatEventType;
  conversation_id: string;
  message_id?: string;
  seq: number;
  truncated?: boolean;

  // per-type payloads
  message?: AssistantMessage;
  delta?: string;
  status?: AssistantStatus;
  tool_call?: ToolCall;
  question?: AssistantQuestion;
  action?: AssistantAction;
  command?: ViewCommand;
  error_detail?: string;
  // conversation_activity
  reason?: string;
  last_seq?: number;
}

const asArray = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/**
 * Coerce message content to a plain markdown string. The backend may serialise
 * content as a string, an array of block objects (`[{type,text}]`), or an
 * object — react-markdown requires a string, so normalise defensively.
 */
function coerceContent(raw: any): string {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    return raw
      .map((b) => (typeof b === 'string' ? b : (b?.text ?? b?.content ?? '')))
      .join('');
  }
  if (typeof raw === 'object') return raw.text ?? raw.content ?? '';
  return String(raw);
}

export function normalizeToolCall(raw: any): ToolCall {
  // Backend status is started|finished|error; collapse error→finished so the
  // spinner stops (the summary text conveys the outcome).
  return {
    id: String(
      raw?.tool_call_id ?? raw?.id ?? raw?.external_id ?? raw?.name ?? ''
    ),
    name: raw?.name ?? '',
    display: raw?.display ?? raw?.name,
    result_summary: raw?.result_summary,
    status: raw?.status === 'started' ? 'started' : 'finished',
  };
}

/**
 * Normalize a Question. `questionMessage` is the external_id of the message that
 * asked it (needed to answer) — it lives on the event/message, not the Question
 * object, so it is passed in. Backend options are `{ key, label }`.
 */
export function normalizeQuestion(
  raw: any,
  questionMessage?: string
): AssistantQuestion | null {
  if (!raw) return null;
  return {
    question_message: String(
      questionMessage ?? raw.question_message ?? raw.external_id ?? ''
    ),
    text: raw.text ?? '',
    options: asArray<any>(raw.options).map((o) =>
      typeof o === 'string'
        ? { value: o, label: o }
        : {
            value: String(o.key ?? o.value ?? o.label),
            label: o.label ?? String(o.key ?? o.value),
            meta: o.meta,
          }
    ),
    multi_select: Boolean(raw.multi_select),
    allow_free_text: Boolean(raw.allow_free_text),
    answered_by: raw.answered_by ?? null,
    selected_options: asArray<string>(raw.selected_options),
  };
}

// Maps ProposedAction.Status (PROPOSED/CONFIRMED/REJECTED/EXPIRED) to the same
// ActionDecision the live websocket path sets optimistically via
// decideAction(). PROPOSED (not yet decided) maps to null.
export function statusToDecision(status: unknown): ActionDecision | null {
  const s = String(status ?? '').toUpperCase();
  if (s === 'CONFIRMED') return 'confirm';
  if (s === 'REJECTED') return 'reject';
  if (s === 'EXPIRED') return 'expired';
  return null;
}

/**
 * After a decideAction request, resolve the decision to apply. A response
 * with no status (the SDK types the endpoint void, so a success may carry
 * none) keeps the optimistic decision instead of clearing it back to
 * undecided; a response with a status (even a server-side override, e.g.
 * already decided/expired) always wins.
 */
export function resolveActualDecision(
  rawStatus: unknown,
  optimisticDecision: ActionDecision | null
): ActionDecision | null {
  return rawStatus != null ? statusToDecision(rawStatus) : optimisticDecision;
}

export function normalizeAction(raw: any): AssistantAction | null {
  if (!raw) return null;
  return {
    external_id: String(raw.external_id ?? raw.id ?? ''),
    kind: raw.kind ?? '',
    title: raw.kind_display ?? raw.title,
    preview: (raw.params ?? raw.preview ?? {}) as Record<string, unknown>,
    note: raw.note,
    decision: raw.decision ?? statusToDecision(raw.status),
  };
}

export function normalizeViewCommand(raw: any): ViewCommand | null {
  if (!raw || !raw.type) return null;
  return raw as ViewCommand;
}

/** Normalize a raw message serialization from REST or a ws frame. */
export function normalizeMessage(raw: any): AssistantMessage {
  return {
    external_id: String(raw?.external_id ?? raw?.id ?? ''),
    client_message_id: raw?.client_message_id ?? undefined,
    role:
      raw?.role === 'USER'
        ? 'USER'
        : raw?.role === 'SYSTEM'
          ? 'SYSTEM'
          : 'ASSISTANT',
    content: coerceContent(raw?.content),
    status: raw?.status,
    seq: Number(raw?.seq ?? 0),
    created_at: raw?.created_at,
    tool_calls: asArray<any>(raw?.tool_calls).map(normalizeToolCall),
    // The question is answered against the message that asked it.
    question: normalizeQuestion(raw?.question, String(raw?.external_id ?? '')),
    actions: asArray<any>(raw?.actions)
      .map((x) => normalizeAction(x))
      .filter(Boolean) as AssistantAction[],
    view_command: normalizeViewCommand(raw?.view_command ?? raw?.command),
    error_detail: raw?.error ?? raw?.error_detail ?? null,
    // The backend tags the follow-up USER message it creates after an action
    // decision via a top-level `kind` (backed by run_metadata server-side,
    // but exposed flat); ordinary turns carry kind "message".
    isActionDecisionFollowUp: Boolean(
      raw?.role === 'USER' && raw?.kind === 'action_decision'
    ),
  };
}

/**
 * Parse a CHAT_EVENT frame. `use-websocket` strips the top-level `message_type`
 * and hands us the rest, so the real event lives under `payload`. Fields are
 * FLAT on the payload (e.g. a tool_call carries `tool_call_id`/`name`/`status`/
 * `result_summary` directly, not nested under a `tool_call` object).
 *
 * Observed frame shape:
 *   { message_type:"CHAT_EVENT", payload:{ v, conversation, event, message,
 *     tool_call_id, name, status, display, result_summary, delta, final_seq, … } }
 */
export function parseChatEvent(data: any): ChatEvent | null {
  if (!data) return null;
  const p = data.payload ?? data;
  const type: ChatEventType = p.event ?? p.type;
  const conversation_id = String(
    p.conversation ?? p.conversation_id ?? p.conversation_external_id ?? ''
  );
  if (!type || !conversation_id) return null;

  const messageRaw = p.message;
  const message_id =
    typeof messageRaw === 'object'
      ? (messageRaw?.external_id ?? messageRaw?.id)
      : (messageRaw ?? p.message_id);

  const ev: ChatEvent = {
    type,
    conversation_id,
    message_id: message_id ? String(message_id) : undefined,
    seq: Number(p.seq ?? p.final_seq ?? 0),
    truncated: Boolean(p.truncated),
  };

  switch (type) {
    case 'assistant_delta':
      ev.delta = p.delta ?? p.text ?? '';
      break;
    case 'assistant_completed':
      // No status field on this event → the turn completed successfully.
      // Failures arrive as a separate `error` event.
      ev.status = 'COMPLETED';
      break;
    case 'tool_call':
      ev.tool_call = normalizeToolCall({
        id: p.tool_call_id ?? p.id,
        name: p.name,
        display: p.display,
        status: p.status,
        result_summary: p.result_summary,
      });
      break;
    case 'question_asked':
      // `question_message` is this event's `message` (the message that asked it).
      ev.question =
        (normalizeQuestion(p.question, ev.message_id) as AssistantQuestion) ??
        undefined;
      break;
    case 'action_confirmation_required':
      // `action` is the ProposedAction external_id (a string); kind/preview are
      // sibling fields on the payload. There is no `message` on this event, so
      // the card attaches to the current assistant turn.
      ev.action = {
        external_id: String(p.action ?? ''),
        kind: p.kind ?? '',
        preview: (p.preview ?? {}) as Record<string, unknown>,
      };
      break;
    case 'view_command':
      ev.command = normalizeViewCommand(p.command) ?? undefined;
      break;
    case 'error':
      ev.error_detail = p.detail ?? p.error ?? '';
      break;
    case 'conversation_activity':
      // A background update landed (possibly for a non-open conversation).
      ev.reason = p.reason;
      ev.last_seq = Number(p.last_seq ?? 0);
      break;
    case 'conversation_state': {
      // { state: { action, kind, status, result_reference } } — carried on
      // `p.state`. Lets every tab viewing this conversation (not just the one
      // that posted the decision) apply the action's actual resulting status.
      const s = p.state ?? {};
      if (s.action) {
        ev.action = {
          external_id: String(s.action),
          kind: s.kind ?? '',
          preview: {},
          decision: statusToDecision(s.status),
        };
      }
      break;
    }
    case 'user_message_ack':
    default:
      break;
  }
  return ev;
}
